import { useState } from 'react';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/base/SchemaOrg';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { brandInfo } from '@/mocks/brand';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;

    // Honeypot check
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qobfknlsngrm4lm2tg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后再试');
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title="关于高柳家——品牌介绍、历史与联系方式 | 官方品牌网站"
        description="了解高柳家的品牌故事、历史沿革、核心价值与口碑声誉。始于1750年，传承九代，以匠心与美学诠释生活的温度与品质。获取高柳家联系方式与品牌资讯。"
        keywords="高柳家,高柳家品牌,高柳家历史,高柳家文化,高柳家传承,高柳家联系方式"
        canonicalUrl="https://takayanagi-family.com/about"
        ogTitle="关于高柳家——品牌介绍、历史与联系方式"
        ogDescription="了解高柳家的品牌故事、历史沿革、核心价值与口碑声誉。始于1750年，传承九代。"
      />
      <SchemaOrg type="AboutPage" data={{
        name: '关于高柳家',
        url: 'https://takayanagi-family.com/about',
        description: brandInfo.description,
        inLanguage: 'zh-Hans',
        about: {
          '@type': 'Organization',
          name: '高柳家',
          foundingDate: '1750',
          description: brandInfo.description,
        },
      }} />

      <main className="pt-16">
        {/* Page Header */}
        <section className="relative w-full h-[240px] md:h-[320px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Traditional%20Japanese%20family%20estate%20entrance%20gate%20with%20wooden%20nameplate%2C%20stone%20path%20leading%20through%20moss%20garden%2C%20ancient%20pine%20trees%2C%20morning%20mist%2C%20peaceful%20and%20dignified%20atmosphere%2C%20cultural%20heritage%20preservation&width=1800&height=600&seq=about-header&orientation=landscape"
            alt="关于高柳家"
            title="高柳家——关于我们"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-3xl md:text-4xl font-heading font-semibold text-white">关于高柳家</h1>
            <p className="mt-3 text-sm md:text-base text-white/80">始于1750年，跨越两个半世纪的传承</p>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />
        </div>

        {/* Brand Intro */}
        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <span className="text-xs font-label tracking-widest text-primary-600 uppercase">Brand Introduction</span>
                <h2 className="mt-3 text-2xl md:text-3xl font-heading font-semibold text-foreground-900">品牌介绍</h2>
                <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">{brandInfo.description}</p>
                <div className="mt-6 space-y-3">
                  <div>
                    <h3 className="text-sm font-heading font-semibold text-foreground-900">品牌使命</h3>
                    <p className="mt-1 text-sm text-foreground-600 leading-relaxed">{brandInfo.mission}</p>
                  </div>
                  <div>
                    <h3 className="text-sm font-heading font-semibold text-foreground-900">品牌愿景</h3>
                    <p className="mt-1 text-sm text-foreground-600 leading-relaxed">{brandInfo.vision}</p>
                  </div>
                </div>
              </div>
              <div className="relative h-[300px] md:h-[400px] rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Interior%20of%20traditional%20Japanese%20wooden%20house%20with%20family%20heirlooms%20displayed%20in%20tokonoma%20alcove%2C%20antique%20ceramics%20and%20calligraphy%20scrolls%2C%20warm%20lantern%20light%2C%20aged%20wood%20textures%2C%20cultural%20heritage%20atmosphere%2C%20elegant%20composition&width=800&height=600&seq=about-brand&orientation=landscape"
                  alt="高柳家品牌介绍"
                  title="高柳家——品牌与文化传承"
                  className="w-full h-full object-cover object-top rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {brandInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-lg bg-background-50">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center text-primary-600">
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <p className="mt-3 text-2xl md:text-3xl font-heading font-semibold text-foreground-900">{stat.value}</p>
                  <p className="mt-1 text-xs md:text-sm text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* History Timeline */}
        <section className="py-12 md:py-16 bg-background-50" id="history">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center">
              <span className="text-xs font-label tracking-widest text-primary-600 uppercase">History</span>
              <h2 className="mt-3 text-2xl md:text-3xl font-heading font-semibold text-foreground-900">历史沿革</h2>
            </div>
            <div className="mt-10 md:mt-12 relative">
              {/* Timeline line */}
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px"></div>
              {brandInfo.history.map((item, i) => (
                <div key={i} className={`relative flex items-start gap-6 pb-8 md:pb-10 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                  {/* Dot */}
                  <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 -translate-x-1/2 mt-1.5 z-10"></div>
                  {/* Content */}
                  <div className={`ml-10 md:ml-0 md:w-[calc(50%-2rem)] ${i % 2 === 0 ? 'md:pr-8 md:text-right' : 'md:pl-8'}`}>
                    <span className="inline-block px-2.5 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">{item.year}</span>
                    <p className="mt-2 text-sm md:text-base text-foreground-700 leading-relaxed">{item.event}</p>
                  </div>
                  {/* Spacer for alternating */}
                  <div className="hidden md:block md:w-[calc(50%-2rem)]"></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center">
              <span className="text-xs font-label tracking-widest text-primary-600 uppercase">Core Values</span>
              <h2 className="mt-3 text-2xl md:text-3xl font-heading font-semibold text-foreground-900">核心价值</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6 mt-10">
              {brandInfo.values.map((v, i) => (
                <div key={i} className="p-5 md:p-6 rounded-lg bg-background-50">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <span className="text-lg font-heading font-semibold">{i + 1}</span>
                  </div>
                  <h3 className="mt-4 text-base font-heading font-semibold text-foreground-900">{v.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{v.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-12 md:py-16 bg-background-50" id="testimonials">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center">
              <span className="text-xs font-label tracking-widest text-primary-600 uppercase">Testimonials</span>
              <h2 className="mt-3 text-2xl md:text-3xl font-heading font-semibold text-foreground-900">口碑声誉</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6 mt-10">
              {brandInfo.testimonials.map((t, i) => (
                <div key={i} className="p-5 md:p-6 rounded-lg bg-background-100">
                  <div className="text-accent-500 text-2xl">&ldquo;</div>
                  <p className="mt-1 text-sm md:text-base text-foreground-700 leading-relaxed">{t.quote}</p>
                  <div className="mt-4 pt-4 border-t border-background-200">
                    <p className="text-sm font-heading font-semibold text-foreground-900">{t.author}</p>
                    <p className="text-xs text-foreground-500">{t.role}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-12 md:py-16 bg-background-100" id="contact">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center">
              <span className="text-xs font-label tracking-widest text-primary-600 uppercase">Contact</span>
              <h2 className="mt-3 text-2xl md:text-3xl font-heading font-semibold text-foreground-900">联系我们</h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 mt-10">
              {/* Contact info */}
              <div>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-heading font-semibold text-foreground-900">地址</h3>
                      <p className="mt-1 text-sm text-foreground-600">{brandInfo.contact.address}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-heading font-semibold text-foreground-900">电话</h3>
                      <p className="mt-1 text-sm text-foreground-600">{brandInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-heading font-semibold text-foreground-900">邮箱</h3>
                      <p className="mt-1 text-sm text-foreground-600">{brandInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-heading font-semibold text-foreground-900">营业时间</h3>
                      <p className="mt-1 text-sm text-foreground-600">{brandInfo.contact.hours}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact form */}
              <div className="rounded-lg bg-background-50 p-5 md:p-6">
                <h3 className="text-base font-heading font-semibold text-foreground-900 mb-5">发送消息</h3>
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="spam-guard"
                  />

                  <div>
                    <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      id="contact-subject"
                      name="subject"
                      required
                      className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-700 mb-1.5">留言</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={4}
                      required
                      maxLength={500}
                      className="w-full px-3 py-2 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    ></textarea>
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="whitespace-nowrap w-full py-2.5 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 text-sm font-medium hover:bg-primary-600 disabled:opacity-50 transition-colors duration-200"
                  >
                    {formStatus === 'loading' ? '发送中...' : '发送消息'}
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-green-600 text-center">消息已成功发送，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}